import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaServer, FaTable, FaProjectDiagram, FaCircle, FaNetworkWired,
  FaWindows, FaLinux, FaUbuntu, FaRedhat, FaCentos, FaApple, FaFreebsd,
  FaChevronRight, FaChevronDown
} from 'react-icons/fa';
import { SiDebian, SiRockylinux } from 'react-icons/si';
import { ServersHierarchyView } from './ServersHierarchyView';
import TableView from './TableView';
import axios from '../utils/axiosConfig';
import './AssetsView.css';

// --- Utilidades de OS ---
const getOSIcon = (os) => {
  if (!os) return FaNetworkWired;
  const l = os.toLowerCase();
  if (l.includes('windows')) return FaWindows;
  if (l.includes('ubuntu')) return FaUbuntu;
  if (l.includes('debian')) return SiDebian;
  if (l.includes('red hat') || l.includes('rhel')) return FaRedhat;
  if (l.includes('centos')) return FaCentos;
  if (l.includes('rocky')) return SiRockylinux;
  if (l.includes('macos') || l.includes('mac os')) return FaApple;
  if (l.includes('freebsd')) return FaFreebsd;
  if (l.includes('linux')) return FaLinux;
  return FaNetworkWired;
};

const getOSColor = (os) => {
  if (!os) return '#6b7280';
  const l = os.toLowerCase();
  if (l.includes('windows')) return '#00adef';
  if (l.includes('ubuntu')) return '#e95420';
  if (l.includes('debian')) return '#a80030';
  if (l.includes('red hat') || l.includes('rhel')) return '#ee0000';
  if (l.includes('centos')) return '#932279';
  if (l.includes('rocky')) return '#10b981';
  if (l.includes('alma')) return '#0eb8e0';
  if (l.includes('macos') || l.includes('mac os')) return '#000';
  if (l.includes('freebsd')) return '#ab2b28';
  if (l.includes('linux')) return '#fcc624';
  return '#6b7280';
};

// --- Celdas especializadas ---

const MetricCell = ({ value }) => {
  if (value == null) return <span className="text-gray-300 text-xs">—</span>;
  const pct = Math.min(Math.max(value, 0), 100);
  const color = pct < 60 ? '#10b981' : pct < 80 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium w-7 text-right flex-shrink-0" style={{ color, fontVariantNumeric: 'tabular-nums' }}>
        {pct.toFixed(0)}%
      </span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-[60px]">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color, transition: 'width 0.5s' }} />
      </div>
    </div>
  );
};

const StatusCell = ({ isOnline }) => (
  <div className="flex justify-center">
    <FaCircle size={8} style={{ color: isOnline ? '#10b981' : '#d1d5db' }} aria-hidden="true" title={isOnline ? 'Online' : 'Offline'} />
  </div>
);

const OSCell = ({ os }) => {
  const Icon = getOSIcon(os);
  const color = getOSColor(os);
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <Icon style={{ color, flexShrink: 0 }} size={15} aria-hidden="true" />
      <span className="text-sm truncate">{os || '—'}</span>
    </div>
  );
};

// --- Componente principal ---

function ServersView() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('tabla');
  const [activeActionMenu, setActiveActionMenu] = useState(null);

  useEffect(() => { fetchServers(); }, []);

  const fetchServers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/servidores/hierarchy');
      setData(response.data);
    } catch (err) {
      console.error('Error fetching servers:', err);
      setError('Error al cargar la infraestructura de servidores');
    } finally {
      setLoading(false);
    }
  };

  // Aplanar jerarquía: hosts como filas padre, VMs como subRows
  const tableData = useMemo(() => {
    const rows = [];
    (data || []).forEach(rack => {
      (rack.hosts || []).forEach(host => {
        rows.push({
          ...host,
          _type: 'host',
          rack_nombre: rack.nombre,
          rack_ubicacion: rack.ubicacion,
          _recursos: [host.cpu, host.ram, host.almacenamiento].filter(Boolean) || [],
          _recursos_raw: true,
          subRows: (host.vms || []).map(vm => ({
            ...vm,
            _type: 'vm',
            host_nombre: host.nombre,
            rack_nombre: rack.nombre,
            _recursos: [vm.vcpu ? `${vm.vcpu} vCPU` : null, vm.vram, vm.almacenamiento].filter(Boolean).join(' • ') || '—',
            _recursos_raw: false,
          })),
        });
      });
    });
    return rows;
  }, [data]);

  // Conteos
  const totalHosts = tableData.length;
  const totalVMs = tableData.reduce((sum, h) => sum + (h.subRows?.length || 0), 0);
  const hostsOnline = tableData.filter(h => h.is_online).length;
  const vmsOnline = tableData.reduce((sum, h) => sum + (h.subRows || []).filter(v => v.is_online).length, 0);

  const [columnVisibility, setColumnVisibility] = useState({
    rack_nombre: true,
    is_online: true,
    nombre: true,
    sistema_operativo: true,
    ip: true,
    _recursos: true,
    last_check: false,
    cpu_usage: true,
    ram_usage: true,
    disk_usage: true,
  });

  // Columnas unificadas host + VM
  const columns = useMemo(() => [
    {
      header: "Rack",
      accessorKey: "rack_nombre",
      size: 100,
      cell: ({ row, getValue }) => {
        if (row.depth > 0) return null; // VMs no muestran rack
        const rack = getValue();
        if (!rack) return '—';
        return (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded bg-gray-700 text-white whitespace-nowrap">
            <FaServer size={9} aria-hidden="true" />
            {rack}
          </span>
        );
      },
    },
    {
      header: "",
      accessorKey: "is_online",
      id: "is_online",
      size: 36,
      maxSize: 36,
      enableSorting: true,
      cell: ({ getValue }) => <StatusCell isOnline={getValue()} />,
    },
    {
      header: "Nombre",
      accessorKey: "nombre",
      size: 200,
      cell: ({ row, getValue }) => {
        const isHost = row.original._type === 'host';
        const canExpand = row.getCanExpand();
        const vmCount = row.original.subRows?.length || 0;
        const name = getValue();

        return (
          <div className="flex items-center gap-1" style={{ paddingLeft: `${row.depth * 24}px` }}>
            {canExpand ? (
              <button
                onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
                className="p-0.5 rounded hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0"
                aria-label={row.getIsExpanded() ? 'Colapsar' : 'Expandir'}
              >
                {row.getIsExpanded()
                  ? <FaChevronDown size={10} aria-hidden="true" />
                  : <FaChevronRight size={10} aria-hidden="true" />
                }
              </button>
            ) : (
              <span className="w-4 flex-shrink-0" />
            )}
            {/* Icono de tipo */}
            {isHost ? (
              <FaServer size={12} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
            ) : (
              <FaNetworkWired size={11} className="text-gray-300 flex-shrink-0" aria-hidden="true" />
            )}
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1">
                <span className={`truncate ${isHost ? 'text-sm font-semibold text-gray-900' : 'text-sm text-gray-600'}`} title={name}>
                  {name}
                </span>
                {isHost && vmCount > 0 && !row.getIsExpanded() && (
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">{vmCount} VM{vmCount > 1 ? 's' : ''}</span>
                )}
              </div>
              {isHost && row.original.serial && (
                <span className="text-[10px] text-gray-400 font-mono truncate">S/N {row.original.serial}</span>
              )}
              {!isHost && row.original.proposito && (
                <span className="text-[10px] text-gray-400 truncate" title={row.original.proposito}>{row.original.proposito}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: "SO",
      accessorKey: "sistema_operativo",
      size: 150,
      cell: ({ getValue }) => <OSCell os={getValue()} />,
    },
    {
      header: "IP",
      accessorKey: "ip",
      size: 130,
      cell: ({ getValue }) => {
        const ip = getValue();
        if (!ip) return <span className="text-gray-300 text-sm">—</span>;
        return <span className="font-mono text-sm">{ip}</span>;
      },
    },
    {
      header: "Recursos",
      accessorKey: "_recursos",
      size: 150,
      cell: ({ row, getValue }) => {
        const isHost = row.original._type === 'host';
        const val = getValue();

        if (isHost) {
          // Host: cada spec en su línea
          const specs = Array.isArray(val) ? val : [];
          return (
            <div className="flex flex-col gap-px leading-tight">
              {specs.map((s, i) => (
                <span key={i} className="text-xs text-gray-600 whitespace-nowrap">{s}</span>
              ))}
              {row.original.marca && (
                <span className="text-[10px] text-gray-400">
                  {[row.original.marca, row.original.modelo].filter(Boolean).join(' ')}
                </span>
              )}
            </div>
          );
        }

        // VM: una línea compacta
        return <span className="text-xs text-gray-500">{val}</span>;
      },
    },
    {
      header: "Actualizado",
      accessorKey: "last_check",
      size: 110,
      cell: ({ getValue }) => {
        const ts = getValue();
        if (!ts) return <span className="text-gray-300 text-xs">—</span>;
        const diff = Date.now() - new Date(ts).getTime();
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(mins / 60);
        const days = Math.floor(hrs / 24);
        let text, color;
        if (mins < 5) { text = 'Ahora'; color = '#10b981'; }
        else if (mins < 60) { text = `${mins} min`; color = '#10b981'; }
        else if (hrs < 24) { text = `${hrs}h`; color = hrs < 6 ? '#f59e0b' : '#ef4444'; }
        else { text = `${days}d`; color = '#ef4444'; }
        return (
          <span className="text-xs font-medium" style={{ color }}>{text}</span>
        );
      },
    },
    {
      header: "CPU",
      accessorKey: "cpu_usage",
      size: 180,
      cell: ({ getValue }) => <MetricCell value={getValue()} />,
    },
    {
      header: "RAM",
      accessorKey: "ram_usage",
      size: 180,
      cell: ({ getValue }) => <MetricCell value={getValue()} />,
    },
    {
      header: "Disco",
      accessorKey: "disk_usage",
      size: 180,
      cell: ({ getValue }) => <MetricCell value={getValue()} />,
    },
  ], []);

  // Filtros
  const availableFilters = useMemo(() => [
    { id: 'rack_nombre', placeholder: 'Todos los racks' },
    { id: 'sistema_operativo', placeholder: 'Todos los SO' },
  ], []);

  // Estilo de fila según tipo
  const getRowClassName = useCallback((original) => {
    if (original._type === 'host') return 'server-host-row';
    return '';
  }, []);

  return (
    <div className="assets-view">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <h2 className="page-title">Servidores</h2>
          {!loading && (
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span>{totalHosts} hosts <span className="text-green-500">({hostsOnline} online)</span></span>
              <span className="text-gray-300">|</span>
              <span>{totalVMs} VMs <span className="text-green-500">({vmsOnline} online)</span></span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Toggle tabla / infraestructura */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('tabla')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'tabla' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Vista tabla"
            >
              <FaTable size={12} aria-hidden="true" />
              Tabla
            </button>
            <button
              onClick={() => setViewMode('infra')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'infra' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Vista infraestructura"
            >
              <FaProjectDiagram size={12} aria-hidden="true" />
              Infraestructura
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {viewMode === 'tabla' ? (
          <motion.div
            key="tabla"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <TableView
              data={tableData}
              columns={columns}
              loading={loading}
              error={error}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              defaultPageSize={50}
              defaultSorting={[{ id: 'rack_nombre', desc: false }]}
              activeActionMenu={activeActionMenu}
              setActiveActionMenu={setActiveActionMenu}
              availableFilters={availableFilters}
              getRowClassName={getRowClassName}
              enableExpansion
              getSubRows={(row) => row.subRows}
              defaultExpanded={true}
            />
          </motion.div>
        ) : (
          <motion.div
            key="infra"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <ServersHierarchyView
              data={data}
              loading={loading}
              error={error}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ServersView;
